import { useEffect, useMemo, useState } from 'react';
import i18n, { t } from 'i18next';

import Info from '@/assets/icons/info.svg?component';
import { Spinner } from '@/components/atoms/Spinner';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import type { ImageFeedback } from '@/domain/imageFeedback';
import type { FullSizeImage } from '@/domain/images';
import { FILTER_TAGS, fullSizeImageMapper } from '@/domain/images';
import type { WelfareData } from '@/domain/welfare/types';
import { useImageState } from '@/hooks/image-state';
import { localizedDateFormat } from '@/utils/date';

import styles from './CageWelfareMaturationDetections.module.scss';

const SHOW_MATURATION_PERCENTAGE_THRESHOLD = 0.005;

export interface CageWelfareMaturationDetectionsPageContentProps {
    isOptoScaleAdmin?: boolean;
    imagesData: FullSizeImage[];
    fishHealthData: WelfareData;
    cageId: number;
    /** The pen's location zone; useImageState reads UTC until it is known. */
    timezone: string | undefined;
    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
}

export default function CageWelfareMaturationDetectionsPageContent(
    props: CageWelfareMaturationDetectionsPageContentProps
) {
    const {
        isOptoScaleAdmin = false,
        onCommitFalseDetection = () => {},
        cageId,
        timezone,
        imagesData,
        fishHealthData,
    } = props;
    const filterTagsList = FILTER_TAGS.filter((filter) => filter.tag === 'maturation_tag');
    const tag = filterTagsList[0];
    const mapper = useMemo(() => fullSizeImageMapper(cageId), [cageId]);

    const maturationImageState = useImageState({
        loading: false,
        mapper: mapper,
        data: imagesData,
        queryParam: 'imageId',
        timezone: timezone,
        filter: tag.filter,
    });

    const [firstSetup, setFirstSetup] = useState(false);

    /* Preparing data for ImageViewer */
    const [selectedImageFilterTag, setSelectedImagedFilterTag] = useState<string>('initial');

    // biome-ignore lint/correctness/useExhaustiveDependencies: We want an effect whenever selectedImageFilterTag changes
    useEffect(() => {
        const lastDay = maturationImageState.dayswithimages.findLast(
            (day) => day.day <= maturationImageState.selectedImageDate
        );
        maturationImageState.onAfterTimelineChangeHandler(
            lastDay?.day ?? maturationImageState.dayswithimages.at(0)?.day ?? ''
        );
    }, [selectedImageFilterTag]);

    const last =
        maturationImageState.dayswithimages[maturationImageState.dayswithimages.length - 1];

    useEffect(() => {
        if (!firstSetup) {
            setFirstSetup(true);
            setSelectedImagedFilterTag('all');
        }
    }, [maturationImageState]);
    const { lastEstimate } = fishHealthData;

    const showMaturationForLowMaturationValues =
        (lastEstimate.matureJaw ?? 0) > SHOW_MATURATION_PERCENTAGE_THRESHOLD;

    return (
        <div className={styles.pageBoxLayout}>
            <ContentBlock size="compact" spacing="none">
                {showMaturationForLowMaturationValues ? (
                    <div>
                        {maturationImageState.loading && <Spinner size="lg" />}
                        {!maturationImageState.loading && (
                            <div>
                                <ImageViewer
                                    key="detections"
                                    variant="maturationDetections"
                                    isOptoScaleAdmin={isOptoScaleAdmin}
                                    onCommitImageFeedback={onCommitFalseDetection}
                                    images={maturationImageState.images}
                                    daysWithImages={maturationImageState.dayswithimages}
                                    onDayChange={maturationImageState.onAfterTimelineChangeHandler}
                                    initialImageIndex={maturationImageState.initialImageIndex}
                                    isFetching={maturationImageState.isFetching}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div>{t('cageWelfare.maturationDetections.thereIsNoDetectedMaturation')}</div>
                )}
            </ContentBlock>
            <ContentBlock
                title={t('common.labels.information')}
                titleIcon={<TitleIcon icon={Info} />}>
                {showMaturationForLowMaturationValues && last && (
                    <MetricSummaryRow
                        flush
                        label={t('common.labels.lastMeasurement')}
                        value={localizedDateFormat({
                            dateString: last.day,
                            onlydate: true,
                            locale: i18n.language,
                        })}
                    />
                )}
            </ContentBlock>
        </div>
    );
}
